'use client';

import { MarketType, PublicKey } from '@drift-labs/sdk';
import {
	ENUM_UTILS,
	MarketId,
	UIMarket,
	UISerializableOrderActionRecord,
	getSortScoreForOrderActionRecords,
	sortUIOrderActionRecords,
} from '@drift/common';
import { useCallback, useContext, useEffect } from 'react';
import { dlog } from 'src/dev';
import { OrderMatcherContext } from 'src/providers/orderRecordMatcherProvider';
import useDriftStore from '../stores/DriftStore/useDriftStore';
import useDriftAccountsStore from '../stores/useDriftAccountsStore';
import useDriftActions from './useDriftActions';
import useLazySubAccounts from './useLazySubAccounts';
import useAppEventEmitter from './useAppEventEmitter';
import { useMarketRecentTradesCache } from './useMarketRecentTradesCache';
import OrderRecordAndActionStateHandler from '../utils/OrderRecordAndActionMatcher';
import { MAX_RECENT_TRADES_TO_CACHE } from '../constants/constants';

/**
 * This hook processes new market trades and adds them to the recent trade histories of the current market and the current user's account.
 * It also handles fetching the initial market history from the history server when the market changes.
 */
const useMarketHistory = () => {
	const setDriftState = useDriftStore((s) => s.set);
	const getAccountsState = useDriftAccountsStore((s) => s.get);
	const setAccountsState = useDriftAccountsStore((s) => s.set);
	const userAccounts = useLazySubAccounts();
	const currentMarket = useDriftStore((s) => s.selectedMarket.current);
	const actions = useDriftActions();
	const orderMatcher = useContext(OrderMatcherContext);
	const appEventEmitter = useAppEventEmitter();

	const tradeMatchesACurrentUserAccount = (
		pubKey = PublicKey.default
	): string | undefined => {
		if (!pubKey) return undefined;
		if (pubKey.equals(PublicKey.default)) return undefined;

		const targetAccount = userAccounts.find((account) =>
			account.pubKey.equals(pubKey)
		);

		if (targetAccount) {
			return targetAccount.userKey;
		}

		return undefined;
	};

	/**
	 * If the new trade for user is newer than any that we currently have in the history or is not in the history at all then we'll add it.
	 * @param trade
	 * @param userKey
	 */
	const addTradesToUserTradehistory = (
		trades: UISerializableOrderActionRecord[],
		userKey: string
	) => {
		const isPredictionMarket =
			trades[0] &&
			new UIMarket(trades[0].marketIndex, trades[0].marketType)
				.isPredictionMarket;

		const historyStoreKey = isPredictionMarket
			? 'predictionsTradeHistory'
			: 'tradeHistory';

		const userTrades = getAccountsState().accounts[userKey]?.[historyStoreKey];

		if (!userTrades) return;

		const lastSeenUserTrade = userTrades.records[0];

		const validNewTrades = trades.filter((trade) => {
			const tradeIsNewer =
				!lastSeenUserTrade ||
				getSortScoreForOrderActionRecords(trade, lastSeenUserTrade) === 1;
			const tradeIsInHistory = userTrades.records.find((tradeInHistory) =>
				orderMatcher.ordersAreIdentical(tradeInHistory, trade)
			);
			return tradeIsNewer || !tradeIsInHistory;
		});

		// If there are new trades, add em
		if (validNewTrades?.length > 1) {
			setAccountsState((s) => {
				const newTrades = sortUIOrderActionRecords([
					...userTrades.records,
					...validNewTrades,
				]) as UISerializableOrderActionRecord[];

				s.accounts[userKey][historyStoreKey].records = newTrades;
				s.accounts[userKey][historyStoreKey].totalCount++;
			});
		} else {
			dlog('initial_load', `No new trades to add`);
		}
	};

	const handleTradesForUserHistory = (
		trades: UISerializableOrderActionRecord[]
	) => {
		// Find trades which belong to the current user
		const tradesAndMatchingUser = trades.map(
			(trade) =>
				[
					trade,
					tradeMatchesACurrentUserAccount(trade.taker ?? undefined) ||
						tradeMatchesACurrentUserAccount(trade.maker ?? undefined),
				] as [UISerializableOrderActionRecord, string | undefined]
		);

		const matchingUserKeys: string[] = [];

		const tradesToAdd = tradesAndMatchingUser.filter(
			([trade, matchingUserKey]) => {
				if (!matchingUserKey) return false;

				// Add trades to order history
				orderMatcher.addActionRecord(trade);

				if (!matchingUserKeys.includes(matchingUserKey)) {
					matchingUserKeys.push(matchingUserKey);
				}

				return true;
			}
		);

		matchingUserKeys.forEach((userKey) => {
			const userTradesToAdd = tradesToAdd
				.filter(([_trade, matchingUserKey]) => matchingUserKey === userKey)
				.map(([trade, _matchingUserKey]) => trade);

			addTradesToUserTradehistory(userTradesToAdd, userKey);
		});
	};

	const { data: cachedMarketTrades, isLoading: isLoadingCachedMarketTrades } =
		useMarketRecentTradesCache(currentMarket?.symbol);

	const handleNewTradesResult = useCallback(
		({
			trades,
			mostRecentTradeScore,
		}: {
			trades: UISerializableOrderActionRecord[];
			mostRecentTradeScore: number;
		}) => {
			if (!trades || trades.length === 0) {
				dlog(`useMarketHistory`, `SkippingHandleNewTradesResult`, trades);
				return;
			}

			setDriftState((s) => {
				const _currentMarket = s.selectedMarket.current;
				const _currentMarketId = _currentMarket?.marketId;
				const tradesArePerp = ENUM_UTILS.match(
					trades[0].marketType,
					MarketType.PERP
				);
				const tradesMarketId = tradesArePerp
					? MarketId.createPerpMarket(trades[0].marketIndex)
					: MarketId.createSpotMarket(trades[0].marketIndex);

				// # Handle if any of the trades affect the current selected market history
				// Skip adding records to market history if they don't match the currently selected market at the time of adding it to the store
				if (!_currentMarketId?.equals(tradesMarketId)) {
					dlog(`useMarketHistory`, `SkippingUpdatingMarketTradeHistory`);
					return;
				}

				const currentTrades = s.marketTradeHistory?.trades ?? [];
				const tradesToClean = [...trades, ...currentTrades];
				const newTradesState =
					OrderRecordAndActionStateHandler.getSortedDedupedTrimmedRecords(
						tradesToClean.filter((trade) =>
							_currentMarketId.equals(
								new MarketId(trade.marketIndex, trade.marketType)
							)
						),
						MAX_RECENT_TRADES_TO_CACHE
					);

				s.marketTradeHistory = {
					market: _currentMarket ?? null,
					trades: newTradesState,
					mostRecentTradeScore,
				};
			});

			// # Handle if any of the trades affect the user's order history
			handleTradesForUserHistory(trades);

			if (currentMarket) {
				appEventEmitter.emit('recentTradesLoaded', currentMarket.key);
			}
			actions.removeLoadingItemFromQueue({ key: 'recentTrades' });
		},
		[currentMarket?.market?.symbol]
	);

	// reset state on market change
	useEffect(() => {
		actions.addLoadingItemToQueue({ key: 'recentTrades' });
		setDriftState((s) => {
			s.marketTradeHistory.market = currentMarket ?? null;
			s.marketTradeHistory.trades = [];
			s.marketTradeHistory.mostRecentTradeScore = 0;
		});
	}, [currentMarket?.market?.symbol]);

	useEffect(() => {
		if (isLoadingCachedMarketTrades) {
			actions.addLoadingItemToQueue({ key: 'recentTrades' });
		}
	}, [isLoadingCachedMarketTrades]);

	useEffect(() => {
		if (cachedMarketTrades && cachedMarketTrades.records.length > 0) {
			actions.addLoadingItemToQueue({ key: 'recentTrades' });
			const trades = sortUIOrderActionRecords(
				cachedMarketTrades.records
			) as UISerializableOrderActionRecord[];
			// TODO: do we need to get the most recent trade score from the server? it's not used in logic or rendered in a component
			handleNewTradesResult({ trades, mostRecentTradeScore: 0 });
		}
	}, [cachedMarketTrades, handleNewTradesResult]);
};

export default useMarketHistory;
